import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Clock, Target, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import toast from 'react-hot-toast';

const PositionsList = ({ positions, onPositionClosed }) => {
  const navigate = useNavigate();
  const getDirectionIcon = (direction) => {
    return direction > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />;
  };

  const getDirectionColor = (direction) => {
    return direction > 0 ? 'text-green-400' : 'text-red-400';
  };

  const getPnLColor = (pnl) => {
    return pnl >= 0 ? 'text-green-400' : 'text-red-400';
  };

  const formatTimeRemaining = (expiresAt) => {
    const now = moment();
    const expiry = moment(expiresAt);
    const duration = moment.duration(expiry.diff(now));
    
    if (duration.asMinutes() < 1) {
      return `${Math.max(0, Math.floor(duration.asSeconds()))}s`;
    } else if (duration.asHours() < 1) {
      return `${Math.floor(duration.asMinutes())}m`;
    } else {
      return `${Math.floor(duration.asHours())}h ${Math.floor(duration.asMinutes() % 60)}m`;
    }
  };

  const handleClosePosition = async (positionId) => {
    try {
      toast.loading('Closing position...', { id: 'closing' });
      
      const response = await fetch('/api/trading/close-position', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ positionId })
      });

      if (response.ok) {
        toast.success('Position closed successfully!', { id: 'closing' });
        if (onPositionClosed) {
          onPositionClosed(positionId);
        }
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to close position', { id: 'closing' });
      }
    } catch (error) {
      toast.error('Error closing position', { id: 'closing' });
      console.error('Close position error:', error);
    }
  };

  // Remove duplicates based on ID and filter out invalid positions
  const uniquePositions = positions.filter((position, index, self) => 
    position && position.id && self.findIndex(p => p && p.id === position.id) === index
  );

  if (uniquePositions.length === 0) {
    return (
      <div className="casino-card p-8 text-center">
        <div className="text-4xl mb-4">📊</div>
        <h3 className="text-xl font-bold text-white mb-2">No Active Positions</h3>
        <p className="text-gray-400">Place your first bet to see positions here!</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {uniquePositions.map((position, index) => (
        <motion.div
          key={position.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className={`position-card ${position.unrealized_pnl >= 0 ? 'winning' : 'losing'} cursor-pointer hover:scale-105 transition-transform duration-200`}
          onClick={() => navigate(`/bet/${position.id}`)}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <span className="text-xl font-bold text-white">{position.symbol}</span>
              <div className={`flex items-center space-x-1 ${getDirectionColor(position.direction)}`}>
                {getDirectionIcon(position.direction)}
                <span className="text-sm font-medium">
                  {position.direction > 0 ? 'LONG' : 'SHORT'}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className={`text-lg font-bold ${getPnLColor(position.unrealized_pnl)}`}>
                {position.unrealized_pnl >= 0 ? '+' : ''}${position.unrealized_pnl?.toFixed(2) || '0.00'}
              </div>
              <div className="text-xs text-gray-400">
                {position.unrealized_pnl >= 0 ? '📈' : '📉'} P&L
              </div>
            </div>
          </div>

          {/* Position Details */}
          <div className="grid grid-cols-2 gap-4 mb-3 text-sm">
            <div>
              <div className="text-gray-400">Bet Amount</div>
              <div className="text-white font-medium">${position.bet_amount}</div>
            </div>
            <div>
              <div className="text-gray-400">Target Gain</div>
              <div className="text-green-400 font-medium">${position.target_gain || 'N/A'}</div>
            </div>
            <div>
              <div className="text-gray-400">Entry Price</div>
              <div className="text-white font-medium">${position.entry_price?.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-gray-400">Timeframe</div>
              <div className="text-white font-medium">{position.timeframe_minutes ? `${position.timeframe_minutes}m` : 'N/A'}</div>
            </div>
            <div>
              <div className="text-gray-400">Instrument</div>
              <div className="text-white font-medium capitalize">{position.instrument_type}</div>
            </div>
            <div>
              <div className="text-gray-400">Success Prob</div>
              <div className="text-blue-400 font-medium">{position.success_probability || 'N/A'}%</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-3">
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>Stop Loss: ${position.stop_loss?.toFixed(2)}</span>
              <span>Take Profit: ${position.take_profit?.toFixed(2)}</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-500 ${
                  position.unrealized_pnl >= 0 ? 'bg-green-500' : 'bg-red-500'
                }`}
                style={{ 
                  width: `${Math.min(100, Math.max(0, 50 + (position.unrealized_pnl / position.bet_amount) * 50))}%` 
                }}
              ></div>
            </div>
          </div>

          {/* Time Remaining */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1 text-xs text-gray-400">
              <Clock className="w-3 h-3" />
              <span>Expires in {formatTimeRemaining(position.expires_at)}</span>
            </div>
            <div className="flex items-center space-x-1 text-xs text-gray-400">
              <Target className="w-3 h-3" />
              <span>{Math.round(position.ai_confidence * 100)}% AI confidence</span>
            </div>
          </div>

          {/* Risk Level Indicator */}
          <div className="mt-2 pt-2 border-t border-gray-700">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400">Risk Level:</span>
              <span className={`font-medium ${
                position.risk_score < 0.3 ? 'text-green-400' :
                position.risk_score < 0.6 ? 'text-yellow-400' :
                position.risk_score < 0.8 ? 'text-orange-400' : 'text-red-400'
              }`}>
                {position.risk_score < 0.3 ? '🟢 Low' :
                 position.risk_score < 0.6 ? '🟡 Medium' :
                 position.risk_score < 0.8 ? '🟠 High' : '🔴 Extreme'}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-3 flex space-x-2" onClick={(e) => e.stopPropagation()}>
            <button 
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-3 rounded text-sm transition-colors flex items-center justify-center"
              onClick={() => navigate(`/bet/${position.id}`)}
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              View Details
            </button>
            <button 
              className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-3 rounded text-sm transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                handleClosePosition(position.id);
              }}
            >
              ❌ Close
            </button>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default PositionsList;